class ClerkRecommendations extends HTMLElement {
    constructor() {
        super();

        this.grid = null;
        this.started = false;
        this.pageSettled = false;
        this.sectionNearViewport = false;
        this.intersectionObserver = null;
    }

    connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;
        this.grid = this.querySelector('[data-clerk-grid]');

        this.waitForPageSettled();
        this.observeViewport();
    }

    waitForPageSettled() {
        const scheduleSettledState = () => {
            let idleCallbackId;
            const markPageSettled = () => {
                if (this.pageSettled) return;

                this.pageSettled = true;
                window.clearTimeout(settledFallbackId);

                if (idleCallbackId && 'cancelIdleCallback' in window) {
                    window.cancelIdleCallback(idleCallbackId);
                }

                this.startWhenReady();
            };

            // The timeout keeps recommendation loading from being blocked forever
            // if an idle period is never available.
            const settledFallbackId = window.setTimeout(markPageSettled, 1500);

            if ('requestIdleCallback' in window) {
                idleCallbackId = window.requestIdleCallback(markPageSettled, { timeout: 1000 });
            }
        };

        if (document.readyState === 'complete') {
            scheduleSettledState();
        } else {
            window.addEventListener('load', scheduleSettledState, { once: true });
        }
    }

    observeViewport() {
        if (!('IntersectionObserver' in window)) {
            this.sectionNearViewport = true;
            this.startWhenReady();
            return;
        }

        this.intersectionObserver = new IntersectionObserver((entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;

            this.sectionNearViewport = true;
            this.startWhenReady();
        }, { rootMargin: '1200px 0px' });

        this.intersectionObserver.observe(this);
    }

    startWhenReady() {
        if (this.started || !this.pageSettled || !this.sectionNearViewport) return;

        this.started = true;
        this.intersectionObserver?.unobserve(this);
        this.call();
    }

    async call() {
        let endpoint = this.endpoint;
        const productView = this.getAttribute('product-view') || 'clerk-item';

        const config = {
            key: window.integrations.clerk.key,
            labels: [],
            attributes: ["handle"]
        }

        config.labels.push(this.label);

        if (this.keywords) config.keywords = this.keywords;
        if (this.limit) config.limit = this.limit;
        if (this.products) config.products = this.products;
        if (this.category) config.category = this.category;

        if (endpoint.includes('/customer/')) {
            if (this.email) {
                config.email = this.email;
            } else {
                endpoint = this.endpoint.replace('/customer/', '/visitor/')
            }
        }

        Clerk('call', endpoint, config,
            async (response) => {
                const productResponses = await Promise.all(response.product_data.map((product) => this.cachedFetch(`${Shopify.routes.root}products/${product.handle}?view=${encodeURIComponent(productView)}`, 'text')));
                this.grid.innerHTML = productResponses.filter(i => !i.includes('<head')).map((productItem) => {
                    return productItem.replaceAll('product-quick-buy----', `product-quick-buy-clerk-recommendations-${encodeURIComponent(endpoint)}-`);
                }).join('')

                Clerk("click", "*[data-clerk-product-id]");
            },
            () => {
                this.section.remove();
            })
    }

    cachedFetch(url, type = 'json') {
        return new Promise(async (resolve) => {
            if (sessionStorage.getItem(`clerk-search-${url}`)) {
                resolve(type === 'json' ? JSON.parse(sessionStorage.getItem(`clerk-search-${url}`)) : sessionStorage.getItem(`clerk-search-${url}`));
            } else {
                const response = await fetch(url).then(r => {
                    if (type === 'json') {
                        return r.json();
                    } else {
                        return r.text()
                    }
                });

                if (type === "text") {
                    if (!response.includes('<head')) sessionStorage.setItem(`clerk-search-${url}`, response);
                } else sessionStorage.setItem(`clerk-search-${url}`, type === 'json' ? JSON.stringify(response.results.products) : response);
                resolve(type === 'json' ? response.results.products : response)
            }
        })
    }

    get label() {
        return this.getAttribute('label');
    }

    get limit() {
        return parseInt(this.getAttribute('limit'));
    }

    get endpoint() {
        return this.getAttribute('endpoint');
    }

    get category() {
        return this.getAttribute('category');
    }

    get keywords() {
        return this.getAttribute('keywords');
    }

    get email() {
        return this.getAttribute('email');
    }

    get products() {
        return this.getAttribute('products') ? this.getAttribute('products').split(',') : null;
    }

    get section () {
        return this.closest('.shopify-section');
    }

    get visitor() {
        return window.sessionStorage.getItem('visitor_id') || 'auto';
    }
}

if (!window.customElements.get("clerk-recommendations")) {
    window.customElements.define("clerk-recommendations", ClerkRecommendations);
}